<template>
  <VFooter
    :class="{'offline-mode': !isOnline || !hasInternet}"
    :style="footerStyle"
    app
    class="pa-1 footer-text"
    height="auto"
  >
    <VContainer class="pa-0" fluid>
      <VRow align="center" class="pa-0" justify="space-between" no-gutters>
        <!-- Left Side: Network & Battery Status -->
        <VCol class="d-flex align-center px-4 gap-3" cols="12" md="4">
          <!-- Network Status -->
          <div class="d-flex align-center ga-2">
            <VIcon
              :class="{'pulse-animation': !isOnline || !hasInternet, 'rotate-animation': connectionStatus === 'checking'}"
              :color="statusColor"
              :icon="statusIcon"
              class="network-icon"
              size="18"
            />
            <span class="text-caption">
              {{ isOnline && hasInternet ? statusText : 'Offline' }}
            </span>
          </div>

          <!-- Copyright -->
          <span class="text-caption">&copy; {{ new Date().getFullYear() }}</span>
        </VCol>

        <!-- Center: Business Name -->
        <VCol class="d-flex justify-center text-uppercase" cols="12" md="4">
          {{ businessName || 'Loading...' }}
        </VCol>

        <!-- Right Side: Status + User + Logout -->
        <VCol class="d-flex justify-end align-center flex-wrap gap-2" cols="12" md="4">
          <!-- Sync Status -->
          <VTooltip location="top">
            <template #activator="{ props }">
              <div class="d-flex align-center ga-1" v-bind="props">
                <VIcon
                  :class="{'rotate-animation': isSyncing}"
                  :color="syncStatusColor"
                  :icon="syncStatusIcon"
                  size="16"
                />
                <span v-if="unsyncedCount > 0" class="text-caption">
                  <VChip color="warning" label size="x-small">
                    {{ unsyncedCount }} unsynced
                  </VChip>
                </span>
                <span v-else-if="isSyncing" class="text-caption">
                  Syncing...
                </span>
              </div>
            </template>
            <span v-if="isSyncing">Syncing orders to server...</span>
            <span v-else-if="unsyncedCount > 0">{{ unsyncedCount }} order{{
                unsyncedCount > 1 ? 's' : ''
              }} pending sync</span>
            <span v-else-if="lastSyncTime">Last synced: {{ formatSyncTime(lastSyncTime) }}</span>
            <span v-else>All orders synced</span>
          </VTooltip>

          <!-- KDS Status -->
          <VTooltip location="top">
            <template #activator="{ props }">
              <span class="text-caption" v-bind="props">KDS:
                <VChip
                  :color="isKdsOnline ? 'success' : 'grey'"
                  label
                  size="x-small"
                >
                  {{ isKdsOnline ? 'Online' : 'Offline' }}
                </VChip>
              </span>
            </template>
            <span>{{ isKdsOnline ? 'Kitchen Display System is connected' : 'Kitchen Display System is not connected' }}</span>
          </VTooltip>

          <!-- Patronect Status -->
          <VTooltip v-if="isPatronectConfigured" location="top">
            <template #activator="{ props }">
              <span class="text-caption" v-bind="props">Patronect:
                <VChip
                  :color="isPatronectOnline ? 'success' : 'grey'"
                  label
                  size="x-small"
                >
                  {{ isPatronectOnline ? 'Online' : 'Offline' }}
                </VChip>
              </span>
            </template>
            <span>{{ isPatronectOnline ? 'Face recognition system is connected' : 'Face recognition system is not connected' }}</span>
          </VTooltip>

          <!-- Socket status -->
          <VTooltip location="top">
            <template #activator="{ props }">
              <span class="text-caption" style="cursor: pointer;" v-bind="props" @click="showConnectionDialog = true">Socket:
                <VChip
                  :color="socketStore.isConnected ? 'success' : (socketStore.isConnecting ? 'orange' : 'error')"
                  label
                  size="x-small"
                >
                  {{
                    socketStore.isConnected ? 'Connected' : (socketStore.isConnecting ? 'Connecting...' : 'Disconnected')
                  }}
                </VChip>
              </span>
            </template>
            <span>Click for diagnostics</span>
          </VTooltip>

          <!-- User chip -->
          <VChip
            v-if="authStore.isAuthenticated"
            color="primary"
            prepend-icon="mdi mdi-account-circle-outline"
            size="small"
            variant="flat"
          >
            {{ userName }}
          </VChip>

          <!-- Fullscreen toggle button -->
          <VTooltip v-if="!isTabletMode" location="top">
            <template #activator="{ props }">
              <VBtn
                :icon="isFullscreen ? 'mdi mdi-fullscreen-exit' : 'mdi mdi-fullscreen'"
                size="small"
                style="font-size: 110% !important;"
                v-bind="props"
                variant="text"
                @click="toggleFullscreen"
              />
            </template>
            <span>{{ isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen' }}</span>
          </VTooltip>

          <!-- Logout button -->
          <VIcon
            v-if="!isTabletMode"
            class="mdi px-4"
            icon="mdi-logout"
            title="Logout"
            variant="text"
            @click="handleLogout"
          />
        </VCol>
      </VRow>
    </VContainer>
  </VFooter>

  <ConnectionStatusDialog v-model="showConnectionDialog"/>
</template>

<script lang="ts" setup>
import {useNetworkMonitor} from '~/composables/useNetworkMonitor'
import {useOrderSync} from '~/composables/useOrderSync'
import {useKdsDetection} from '~/composables/useKdsDetection'
import {usePatronect} from '~/composables/usePatronect'
import {DateTime} from 'luxon'

const socketStore = useSocketStore()
const authStore = useUserStore()
const {isTabletMode} = useTabletMode()
const {isKdsOnline, startListening: startKdsDetection} = useKdsDetection()
const {isConfigured: isPatronectConfigured, isConnected: isPatronectConnected, testConnection: testPatronectConnection, loadSettings: loadPatronectSettings} = usePatronect()

const showConnectionDialog = ref(false)
const isPatronectOnline = ref(false)
let patronectPingInterval: ReturnType<typeof setInterval> | null = null

const {
  isOnline,
  hasInternet,
  connectionStatus,
  statusColor,
  statusIcon,
  statusText,
} = useNetworkMonitor()

const {
  isSyncing,
  lastSyncTime,
  unsyncedCount,
  updateUnsyncedCount
} = useOrderSync()

const businessName = computed(() => authStore.activeBusinessName || 'Your Business')

const userName = computed(() => {
  if (!authStore.profile) return 'User'

  const first = authStore.profile.first_name || ''
  const last = authStore.profile.last_name || ''

  if (first && last) {
    return `${first} ${last}`
  } else if (first) {
    return first
  } else if (last) {
    return last
  }

  return authStore.profile.email || 'User'
})

const syncStatusIcon = computed(() => {
  if (isSyncing.value) return 'mdi mdi-sync'
  if (unsyncedCount.value > 0) return 'mdi mdi-cloud-upload-outline'
  return 'mdi mdi-cloud-check-outline'
})

const syncStatusColor = computed(() => {
  if (isSyncing.value) return 'info'
  if (unsyncedCount.value > 0) return 'warning'
  return 'success'
})

const formatSyncTime = (isoString: string) => {
  return DateTime.fromISO(isoString).toRelative()
}

const handleLogout = () => {
  authStore.logout()
}

const isFullscreen = ref(false)
const isAndroid = ref(false)

const footerStyle = computed(() => {
  if (isAndroid.value) {
    return {
      paddingBottom: '48px'
    }
  }
  return {}
})

const toggleFullscreen = async () => {
  try {
    const {getCurrentWindow} = await import('@tauri-apps/api/window')
    const window = getCurrentWindow()
    const currentState = await window.isFullscreen()
    await window.setFullscreen(!currentState)
    isFullscreen.value = !currentState
    console.log('[Footer] Fullscreen toggled:', !currentState)
  } catch (error) {
    console.log('[Footer] Fullscreen not available (running in browser):', error)
  }
}

const checkFullscreenState = async () => {
  try {
    const {getCurrentWindow} = await import('@tauri-apps/api/window')
    const window = getCurrentWindow()
    isFullscreen.value = await window.isFullscreen()
  } catch (error) {
    console.log('[Footer] Running in browser, fullscreen check skipped')
  }
}

const pingPatronect = async () => {
  if (!isPatronectConfigured.value) {
    isPatronectOnline.value = false
    return
  }
  const result = await testPatronectConnection()
  isPatronectOnline.value = result.success
}

onMounted(async () => {
  await updateUnsyncedCount()
  await checkFullscreenState()
  startKdsDetection()

  await loadPatronectSettings()
  await pingPatronect()
  patronectPingInterval = setInterval(pingPatronect, 30000)

  if (import.meta.client) {
    console.log('[Footer] Running in browser, fullscreen check skipped')
  }
})

onUnmounted(() => {
  if (patronectPingInterval) {
    clearInterval(patronectPingInterval)
    patronectPingInterval = null
  }
})

watch(() => socketStore.isConnected, async (connected) => {
  if (connected) {
    await updateUnsyncedCount()
  }
})
</script>

<style scoped>
.footer-text * {
  font-size: 90% !important;
  font-weight: normal !important;
}

.footer-text {
  background: rgba(var(--v-theme-surface), 0.95) !important;
  backdrop-filter: blur(8px);
  border-top: 1px solid rgba(var(--v-border-color), 0.12);
  transition: all 0.3s ease;
}

.footer-text.offline-mode {
  background: rgba(var(--v-theme-error), 0.1) !important;
  border-top-color: rgb(var(--v-theme-error));
  animation: offline-pulse 2s ease-in-out infinite;
}

@keyframes offline-pulse {
  0%, 100% {
    border-top-color: rgba(var(--v-theme-error), 0.3);
  }
  50% {
    border-top-color: rgba(var(--v-theme-error), 0.8);
  }
}

@keyframes pulse-animation {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.1);
  }
}

@keyframes rotate-animation {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.pulse-animation {
  animation: pulse-animation 1.5s ease-in-out infinite;
}

.rotate-animation {
  animation: rotate-animation 2s linear infinite;
}

.network-icon {
  transition: all 0.3s ease;
}
</style>
